import { useEffect, useRef, useState } from "react";
import { cn } from "@/utils/cn";
import { Icon } from "@/components/Icon/Icon";
import type { TabStyle } from "@/utils/types/ui";
import type { Variant, VariantStats } from "@/utils/types/variant";

const formatLoss = (v: number | null | undefined) =>
  v == null ? "—" : v.toFixed(1);
const formatCost = (v: number | null | undefined) =>
  v == null ? "—" : `$${v.toFixed(2)}`;

// Color-only delta (lower is better) for KPI numbers vs the active baseline.
function deltaTone(
  val: number | null | undefined,
  base: number | null | undefined,
  lowerIsBetter = true
): { color: string } {
  if (val == null || base == null) return { color: "var(--ink-4)" };
  if (Math.abs(val - base) < 0.005 * Math.max(1, Math.abs(base))) {
    return { color: "var(--ink-4)" };
  }
  const better = lowerIsBetter ? val < base : val > base;
  return { color: better ? "var(--brand-green)" : "#c92619" };
}

type FormulationTabProps = {
  variant: Variant;
  active: boolean;
  canRemove: boolean;
  style: TabStyle;
  showKpi?: boolean;
  stats?: VariantStats;
  baseStats?: VariantStats;
  locked?: boolean;
  onClick: () => void;
  onRename: (name: string) => void;
  onRemove: () => void;
};

export function FormulationTab({
  variant,
  active,
  canRemove,
  style,
  showKpi = false,
  stats,
  baseStats,
  locked = false,
  onClick,
  onRename,
  onRemove,
}: FormulationTabProps) {
  const [editing, setEditing] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [draft, setDraft] = useState(variant.name);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wasActiveOnDownRef = useRef(false);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    const v = draft.trim();
    if (v && v !== variant.name) onRename(v);
    setEditing(false);
  };

  const lossDelta =
    baseStats && stats
      ? deltaTone(stats.loss, baseStats.loss, true)
      : { color: "var(--ink-4)" };
  const costDelta =
    baseStats && stats
      ? deltaTone(stats.cost?.usdPer100g, baseStats.cost?.usdPer100g, true)
      : { color: "var(--ink-4)" };

  // Per-style heights — minimal stretches the bar, pill/card sit centered.
  const isMinimal = style === "minimal";
  const isCard = style === "card";
  const tabHeight = isMinimal
    ? showKpi
      ? 56
      : 38
    : isCard
    ? showKpi
      ? 52
      : 28
    : showKpi
    ? 48
    : 26;

  return (
    <div
      onMouseDown={() => {
        wasActiveOnDownRef.current = active;
      }}
      onClick={() => {
        if (editing) return;
        if (wasActiveOnDownRef.current && active) {
          setDraft(variant.name);
          setEditing(true);
        } else {
          onClick();
        }
      }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      title={variant.prompt ? `Prompt: ${variant.prompt}` : undefined}
      className={cn("fm-tab", style, active && "active")}
      style={{ height: tabHeight }}
    >
      <div className="fm-tab-row1">
        {isMinimal && <span className="fm-tab-dot" />}

        {editing ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              else if (e.key === "Escape") {
                setDraft(variant.name);
                setEditing(false);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            className="fm-tab-rename-input"
            style={{ width: Math.max(40, draft.length * 7 + 14) }}
          />
        ) : (
          <span style={{ lineHeight: 1 }}>{variant.name}</span>
        )}

        {variant.prompt && !editing && (
          <span className="fm-tab-prompt-mark">
            <Icon name="sparkle" size={10} />
          </span>
        )}

        {locked && !editing && (
          <span
            title={`Batch started — ${variant.name} is locked from edits`}
            className="fm-tab-lock"
          >
            <Icon name="beaker" size={9} />
          </span>
        )}
      </div>

      {showKpi && !editing && stats && (
        <div className="fm-tab-kpi mono">
          <span className="fm-tab-kpi-cell">
            <span className="fm-tab-kpi-key">L²</span>
            <span
              className="fm-tab-kpi-val"
              style={{ color: active ? "var(--ink-2)" : lossDelta.color }}
            >
              {formatLoss(stats.loss)}
            </span>
          </span>
          <span className="fm-tab-kpi-cell">
            <span className="fm-tab-kpi-key">$</span>
            <span
              className="fm-tab-kpi-val"
              style={{ color: active ? "var(--ink-2)" : costDelta.color }}
            >
              {formatCost(stats.cost?.usdPer100g)}
            </span>
          </span>
        </div>
      )}

      {canRemove && hovering && !editing && (
        <button
          type="button"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          title="Delete this formulation"
          className="fm-tab-delete"
        >
          <Icon name="x" size={8} />
        </button>
      )}
    </div>
  );
}
